import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { WorkOrderData } from '../services/api';

// Extend jsPDF type to include autoTable and setLineDash
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable?: {
      finalY: number;
    };
    setLineDash: (pattern: number[], phase?: number) => jsPDF;
  }
}

const formatValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '--';
  return value.toString();
};

const formatAxis = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '--';
  return `${value}°`;
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '0.00';
  return new Intl.NumberFormat('es-GT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '--';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '--';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day} / ${month} / ${year}`;
};

export const generateWorkOrderPDF = (data: WorkOrderData, returnBlob: boolean = false): void | Blob => {
  // Create PDF in landscape A5 format (210mm x 148mm)
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a5',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 10;

  // Header - Clinic name and date
  doc.setFontSize(16);
  doc.setTextColor(43, 124, 238); // Primary blue color
  doc.setFont('helvetica', 'bold');
  doc.text(data.clinic.name || 'OpticClinic', 10, yPos);

  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  const dateText = formatDate(data.visit.created_at);
  doc.text(dateText, pageWidth - 10, yPos, { align: 'right' });

  yPos += 8;

  // Divider line
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.2);
  doc.line(10, yPos, pageWidth - 10, yPos);

  yPos += 5;

  // Patient information table
  autoTable(doc, {
    startY: yPos,
    head: [['NIT', 'PACIENTE', 'TEL']],
    body: [
      [
        data.patient.nit || 'C/F',
        data.patient.full_name,
        data.patient.phone,
      ],
    ],
    theme: 'grid',
    headStyles: {
      fontSize: 7,
      fontStyle: 'bold',
      textColor: [71, 85, 105],
      fillColor: [248, 250, 252],
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [30, 41, 59],
      fillColor: [248, 250, 252],
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
    },
    margin: { left: 10, right: 10 },
    tableWidth: pageWidth - 20,
  });

  yPos = (doc as any).lastAutoTable.finalY;

  // Address row
  autoTable(doc, {
    startY: yPos,
    head: [['DIRECCIÓN']],
    body: [[data.patient.address]],
    theme: 'grid',
    headStyles: {
      fontSize: 7,
      fontStyle: 'bold',
      textColor: [71, 85, 105],
      fillColor: [248, 250, 252],
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [30, 41, 59],
      fillColor: [248, 250, 252],
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
    },
    margin: { left: 10, right: 10 },
    tableWidth: pageWidth - 20,
  });

  yPos = (doc as any).lastAutoTable.finalY + 5;

  // Prescription table and DIP box side by side
  const rxTableWidth = pageWidth - 60;
  
  // Prescription table
  autoTable(doc, {
    startY: yPos,
    head: [
      ['RX', 'OJO', 'ESFERA', 'CIL', 'EJE', 'ADD'],
    ],
    body: [
      [
        'LEJOS',
        'OD',
        formatValue(data.refraction?.far.od.sphere),
        formatValue(data.refraction?.far.od.cylinder),
        formatAxis(data.refraction?.far.od.axis),
        formatValue(data.refraction?.far.od.add),
      ],
      [
        '',
        'OI',
        formatValue(data.refraction?.far.oi.sphere),
        formatValue(data.refraction?.far.oi.cylinder),
        formatAxis(data.refraction?.far.oi.axis),
        formatValue(data.refraction?.far.oi.add),
      ],
      [
        'CERCA',
        'OD',
        formatValue(data.refraction?.near.od.sphere),
        formatValue(data.refraction?.near.od.cylinder),
        formatAxis(data.refraction?.near.od.axis),
        formatValue(data.refraction?.near.od.add),
      ],
      [
        '',
        'OI',
        formatValue(data.refraction?.near.oi.sphere),
        formatValue(data.refraction?.near.oi.cylinder),
        formatAxis(data.refraction?.near.oi.axis),
        formatValue(data.refraction?.near.oi.add),
      ],
    ],
    theme: 'grid',
    headStyles: {
      fontSize: 7,
      fontStyle: 'bold',
      textColor: [71, 85, 105],
      fillColor: [255, 255, 255],
      halign: 'center',
      lineColor: [203, 213, 225],
      lineWidth: 0.3,
    },
    bodyStyles: {
      fontSize: 8,
      textColor: [71, 85, 105],
      halign: 'center',
      lineColor: [203, 213, 225],
      lineWidth: 0.3,
    },
    columnStyles: {
      0: { fontStyle: 'bold', fontSize: 9, textColor: [30, 41, 59] },
      1: { fontStyle: 'bold', fontSize: 8, textColor: [51, 65, 85] },
    },
    margin: { left: 10 },
    tableWidth: rxTableWidth,
  });

  // DIP box (positioned to the right of the Rx table)
  const dipBoxX = 10 + rxTableWidth + 5;
  const dipBoxY = yPos;
  const dipBoxWidth = 35;
  const dipBoxHeight = 20;

  doc.setFillColor(236, 253, 245); // Light green background
  doc.setDrawColor(167, 243, 208); // Green border
  doc.setLineWidth(0.2);
  doc.rect(dipBoxX, dipBoxY, dipBoxWidth, dipBoxHeight, 'FD');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(4, 120, 87);
  doc.text('DIP', dipBoxX + dipBoxWidth / 2, dipBoxY + 6, { align: 'center' });

  const dipValue = data.visual_acuity?.od_dip_mm || data.visual_acuity?.oi_dip_mm;
  doc.setFontSize(12);
  doc.setTextColor(5, 150, 105);
  doc.text(
    dipValue ? `${dipValue} mm` : 'N/A',
    dipBoxX + dipBoxWidth / 2,
    dipBoxY + 14,
    { align: 'center' }
  );

  yPos = (doc as any).lastAutoTable.finalY + 5;

  // Products section
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text('LENTE:', 12, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(data.sales?.lens_description || '', 30, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`Q ${formatCurrency(data.sales?.lens_price)}`, pageWidth - 12, yPos, {
    align: 'right',
  });

  yPos += 5;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text('ARMAZÓN:', 12, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(data.sales?.frame_description || '', 30, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`Q ${formatCurrency(data.sales?.frame_price)}`, pageWidth - 12, yPos, {
    align: 'right',
  });

  yPos += 5;

  // Divider line
  doc.setDrawColor(226, 232, 240);
  doc.line(10, yPos, pageWidth - 10, yPos);

  yPos += 5;

  // Total section
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 116, 139);
  doc.text('Total a Pagar', pageWidth - 40, yPos);

  doc.setFontSize(20);
  doc.setTextColor(16, 185, 129);
  doc.text(
    `Q ${formatCurrency(data.sales?.grand_total)}`,
    pageWidth - 40,
    yPos + 7
  );

  yPos += 10;

  // Divider line (dashed)
  doc.setLineDash([1, 1]);
  doc.line(10, yPos, pageWidth - 10, yPos);
  doc.setLineDash([]);

  yPos += 4;

  // Observations section
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 116, 139);
  doc.text('OBSERVACIONES', 10, yPos);

  yPos += 4;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  
  const observations: string[] = [];
  if (data.visit.diagnosis) {
    observations.push(data.visit.diagnosis);
  }
  if (data.sales?.extras && data.sales.extras.length > 0) {
    const extrasText = data.sales.extras.map(e => e.description).join(', ');
    observations.push(extrasText);
  }
  
  const observationsText = observations.length > 0 ? observations.join(' | ') : 'Ninguna';
  const splitText = doc.splitTextToSize(observationsText, pageWidth - 20);
  doc.text(splitText, 10, yPos);

  if (returnBlob) {
    // Return as Blob for API upload
    return doc.output('blob') as Blob;
  }

  // Save and download the PDF
  const fileName = `orden-trabajo-${data.patient.full_name.replace(/\s+/g, '-')}-${new Date().getTime()}.pdf`;
  doc.save(fileName);
};
